/**
 * Dynamic Table Frontend JavaScript
 * 
 * Handles tab switching and responsive transposition for dynamic tables
 */
(function() {
    'use strict';

    const MOBILE_BREAKPOINT = 768;

    /**
     * Initialize dynamic tables
     */
    function initDynamicTables() {
        const tables = document.querySelectorAll('.dynamic-table-wrapper');
        
        tables.forEach(function(table) {
            initTabNavigation(table);
            initTabOverflowIndicator(table);
            
            // Initialize transposed tables for scroll-sticky mode
            if (table.classList.contains('dt-responsive-scroll-sticky')) {
                initTransposedTable(table);
            }
        });
    }

    /**
     * Initialize tab overflow indicator (fade effect)
     * @param {HTMLElement} wrapper - The table wrapper element
     */
    function initTabOverflowIndicator(wrapper) {
        const tabNav = wrapper.querySelector('.dt-tabs-navigation');
        if (!tabNav) return;
        
        // Wrap tab navigation if not already wrapped
        if (!tabNav.parentElement.classList.contains('dt-tabs-navigation-wrapper')) {
            const navWrapper = document.createElement('div');
            navWrapper.className = 'dt-tabs-navigation-wrapper';
            tabNav.parentNode.insertBefore(navWrapper, tabNav);
            navWrapper.appendChild(tabNav);
        }
        
        const navWrapper = tabNav.parentElement;
        
        function checkOverflow() {
            const hasOverflow = tabNav.scrollWidth > tabNav.clientWidth;
            const isScrolledToEnd = tabNav.scrollLeft + tabNav.clientWidth >= tabNav.scrollWidth - 5;
            
            if (hasOverflow && !isScrolledToEnd) {
                navWrapper.classList.add('has-overflow');
            } else {
                navWrapper.classList.remove('has-overflow');
            }
        }
        
        // Check on load and resize
        checkOverflow();
        window.addEventListener('resize', debounce(checkOverflow, 150));
        
        // Check on scroll
        tabNav.addEventListener('scroll', debounce(checkOverflow, 50));
    }

    /**
     * Initialize transposed table for scroll-sticky responsive mode
     * @param {HTMLElement} wrapper - The table wrapper element
     */
    function initTransposedTable(wrapper) {
        const panels = wrapper.querySelectorAll('.dt-tab-panel');
        
        panels.forEach(function(panel) {
            const originalTable = panel.querySelector('.dynamic-table');
            if (!originalTable) return;
            
            // Create transposed version
            const transposedWrapper = createTransposedTable(originalTable, wrapper);
            if (transposedWrapper) {
                // Insert after original table scroll wrapper
                const scrollWrapper = panel.querySelector('.dt-table-scroll');
                if (scrollWrapper) {
                    scrollWrapper.after(transposedWrapper);
                } else {
                    originalTable.after(transposedWrapper);
                }
            }
        });
        
        // Handle responsive display
        handleResponsiveDisplay(wrapper);
        window.addEventListener('resize', debounce(function() {
            handleResponsiveDisplay(wrapper);
        }, 150));
    }

    /**
     * Create a transposed version of the table
     * @param {HTMLElement} originalTable - The original table element
     * @param {HTMLElement} wrapper - The table wrapper for getting styles
     * @return {HTMLElement|null} - The transposed table wrapper or null
     */
    function createTransposedTable(originalTable, wrapper) {
        const thead = originalTable.querySelector('thead');
        const tbody = originalTable.querySelector('tbody');
        
        if (!tbody) return null;
        
        // Get header rows and body rows
        const headerRows = thead ? thead.querySelectorAll('tr') : [];
        const bodyRows = tbody.querySelectorAll('tr');
        
        if (bodyRows.length === 0) return null;
        
        // Find the last header row (contains actual column labels)
        let columnLabels = [];
        if (headerRows.length > 0) {
            const lastHeaderRow = headerRows[headerRows.length - 1];
            const headerCells = lastHeaderRow.querySelectorAll('th, td');
            headerCells.forEach(function(cell) {
                const colspan = parseInt(cell.getAttribute('colspan')) || 1;
                const content = cell.innerHTML;
                // Add label for each colspan
                for (let i = 0; i < colspan; i++) {
                    columnLabels.push(content);
                }
            });
        }
        
        // If no headers, use first body row as labels
        if (columnLabels.length === 0 && bodyRows.length > 0) {
            const firstRow = bodyRows[0];
            const cells = firstRow.querySelectorAll('th, td');
            cells.forEach(function(cell) {
                columnLabels.push(cell.innerHTML);
            });
        }
        
        // Collect body data - each row becomes a column in transposed view
        const bodyData = [];
        bodyRows.forEach(function(row) {
            const rowData = [];
            const cells = row.querySelectorAll('th, td');
            cells.forEach(function(cell) {
                rowData.push(cell.innerHTML);
            });
            bodyData.push(rowData);
        });
        
        if (bodyData.length === 0 || columnLabels.length === 0) return null;
        
        // Create transposed table
        // Columns of original become rows
        // Rows of original become columns
        const transposedWrapper = document.createElement('div');
        transposedWrapper.className = 'dt-transposed-scroll';
        
        const transposedTable = document.createElement('table');
        transposedTable.className = 'dynamic-table dt-transposed';
        
        const newTbody = document.createElement('tbody');
        
        // Number of columns in transposed = number of body rows + 1 (for label)
        // Number of rows in transposed = number of columns in original
        
        for (let col = 0; col < columnLabels.length; col++) {
            const tr = document.createElement('tr');
            
            // First cell: the column label (becomes row header)
            const th = document.createElement('th');
            th.innerHTML = columnLabels[col];
            tr.appendChild(th);
            
            // Rest of cells: data from each body row for this column
            bodyData.forEach(function(rowData) {
                const td = document.createElement('td');
                td.innerHTML = rowData[col] || '';
                tr.appendChild(td);
            });
            
            newTbody.appendChild(tr);
        }
        
        transposedTable.appendChild(newTbody);
        transposedWrapper.appendChild(transposedTable);
        
        return transposedWrapper;
    }

    /**
     * Handle responsive display - show/hide original vs transposed
     * @param {HTMLElement} wrapper - The table wrapper
     */
    function handleResponsiveDisplay(wrapper) {
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        const scrollWrappers = wrapper.querySelectorAll('.dt-table-scroll');
        const transposedWrappers = wrapper.querySelectorAll('.dt-transposed-scroll');
        
        scrollWrappers.forEach(function(el) {
            el.style.display = isMobile ? 'none' : '';
        });
        
        transposedWrappers.forEach(function(el) {
            el.style.display = isMobile ? '' : 'none';
        });
        
        // Also hide original tables not in scroll wrapper
        const originalTables = wrapper.querySelectorAll('.dt-tab-panel > .dynamic-table:not(.dt-transposed)');
        originalTables.forEach(function(el) {
            el.style.display = isMobile ? 'none' : '';
        });
    }

    /**
     * Debounce function
     */
    function debounce(func, wait) {
        let timeout;
        return function() {
            const context = this, args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(function() {
                func.apply(context, args);
            }, wait);
        };
    }

    /**
     * Initialize tab navigation for a table
     * @param {HTMLElement} wrapper - The table wrapper element
     */
    function initTabNavigation(wrapper) {
        const tabButtons = wrapper.querySelectorAll('.dt-tab-button');
        const tabPanels = wrapper.querySelectorAll('.dt-tab-panel');

        if (!tabButtons.length || !tabPanels.length) {
            return;
        }

        tabButtons.forEach(function(button) {
            button.addEventListener('click', function() {
                const tabIndex = this.getAttribute('data-tab');
                
                // Update button states
                tabButtons.forEach(function(btn) {
                    btn.classList.remove('active');
                    btn.setAttribute('aria-selected', 'false');
                });
                this.classList.add('active');
                this.setAttribute('aria-selected', 'true');

                // Update panel visibility
                tabPanels.forEach(function(panel) {
                    const panelIndex = panel.getAttribute('data-tab');
                    if (panelIndex === tabIndex) {
                        panel.classList.add('active');
                        panel.removeAttribute('hidden');
                    } else {
                        panel.classList.remove('active');
                        panel.setAttribute('hidden', '');
                    }
                });
            });

            // Keyboard navigation
            button.addEventListener('keydown', function(e) {
                let targetButton = null;
                const buttons = Array.from(tabButtons);
                const currentIndex = buttons.indexOf(this);

                switch (e.key) {
                    case 'ArrowLeft':
                        targetButton = buttons[currentIndex - 1] || buttons[buttons.length - 1];
                        break;
                    case 'ArrowRight':
                        targetButton = buttons[currentIndex + 1] || buttons[0];
                        break;
                    case 'Home':
                        targetButton = buttons[0];
                        break;
                    case 'End':
                        targetButton = buttons[buttons.length - 1];
                        break;
                }

                if (targetButton) {
                    e.preventDefault();
                    targetButton.focus();
                    targetButton.click();
                }
            });
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initDynamicTables);
    } else {
        initDynamicTables();
    }

    // Expose globally so other scripts can re-init after dynamic HTML injection
    window.CA_DynamicTable = { init: initDynamicTables };

})();
